import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface StatCardProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
  value: React.ReactNode;
  icon?: LucideIcon;
  /** Small secondary line under the value (e.g. "3 active"). */
  hint?: React.ReactNode;
}

/**
 * Clay metric tile — chunky raised surface with a soft icon badge, big value,
 * and an optional hint. The building block for dashboards.
 */
export function StatCard({ label, value, icon: Icon, hint, className, ...props }: StatCardProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-4 rounded-card border-2 border-brand-outline-variant/20 bg-card p-5 shadow-clay transition-shadow duration-normal hover:shadow-clay-lg',
        className,
      )}
      {...props}
    >
      {Icon && (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-button accent-gradient text-primary-foreground shadow-clay-sm">
          <Icon className="h-6 w-6" />
        </div>
      )}
      <div className="min-w-0">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <p className="font-heading text-2xl font-extrabold leading-tight text-foreground">{value}</p>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
    </div>
  );
}
